/* eslint-disable max-len */
import { ReactElement, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MAX_DATE } from 'src/types/Constants';
import FormDateSelector from '../../../../components/FormInputs/FormDateSelector/FormDateSelector';
import FormDropDown from '../../../../components/FormInputs/FormDropdown/FormDropdown';
import FormTextbox from '../../../../components/FormInputs/FormTextbox/FormTextbox';
import ManualInputPage from '../../../../components/ManualInputPage/ManualInputPage';
import Panel from '../../../../components/Panel/Panel';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  clearSummerActivity,
  saveSummerActivity,
  selectSummerActivityDataEntryMode,
  selectSummerActivityFormViewModel,
  selectSummerActivityLoadingFormViewModel,
  selectSummerActivityLoadingSummerActivity,
  selectSummerActivitySummerActivity,
  setSummerActivityDataEntryMode,
} from '../../../../redux/slices/summerActivitySlice';
import { fetchActiveSchoolYears } from '../../../../redux/thunks/activeSchoolYearThunks';
import {
  fetchSummerActivityById,
  fetchSummerActivityFormViewModel,
  fetchSummerActivitySchoolYears,
} from '../../../../redux/thunks/summerActivityThunks';
import { abortPromiseOnUnmount } from '../../../../services/base.service';
import { appRoutePaths } from '../../../../services/route.service';
import {
  createSummerActivity,
  updateSummerActivity,
} from '../../../../services/SummerActivity.service';
import { GLOBAL_DEBUG_MODE } from '../../../../testing/debugFunctions';
import { DataEntryMode } from '../../../../types/DataEntryMode';
import { Tabs_STUDENT } from '../../../../types/NavigationTabs';
import { PanelType } from '../../../../types/PanelType';
import { ManualInputCrudAction } from '../../../../types/propTypes/ManualInputPagePropTypes';
import { SummerActivityFormViewModel } from '../../../../types/SummerActivityFormViewModel';
import { SummerActivityModel } from '../../../../types/SummerActivityModel';
import { SummerActivityValidationSchema } from '../../../../types/Validation/SummerActivityValidationSchema';
import { buildUserAccessNavigationTabItems } from '../../../../utilities/routeUtilities';
import './SummerActivityInput.css';

const SummerActivityInput = (): ReactElement => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const summerActivityModel: SummerActivityModel = useAppSelector(
    selectSummerActivitySummerActivity
  );
  const formViewModel: SummerActivityFormViewModel = useAppSelector(
    selectSummerActivityFormViewModel
  );
  const isLoadingFormViewModel = useAppSelector(
    selectSummerActivityLoadingFormViewModel
  );
  const isLoadingSummerActivityModel = useAppSelector(
    selectSummerActivityLoadingSummerActivity
  );
  const dataEntryMode: DataEntryMode = useAppSelector(
    selectSummerActivityDataEntryMode
  );
  const isLoading = isLoadingSummerActivityModel || isLoadingFormViewModel;
  const buildReturnUrl = (): string => {
    const currentSearchParams = new URLSearchParams(location.search);
    const urlSchoolYear = currentSearchParams.get('schoolYear');
    const urlSchool = currentSearchParams.get('school');

    if (urlSchoolYear || urlSchool) {
      const searchParams = new URLSearchParams();
      if (urlSchoolYear) searchParams.set('schoolYear', urlSchoolYear);
      if (urlSchool) searchParams.set('school', urlSchool);

      const queryString = searchParams.toString();
      return `${appRoutePaths.SummerActivityList}${queryString ? `?${queryString}` : ''
        }`;
    }
    return appRoutePaths.SummerActivityList;
  };

  const supportedNonGenericErrors = [
    {
      statusCode: 400,
    },
  ];
  useEffect(() => {
    const promise: unknown = dispatch(fetchActiveSchoolYears());
    return () => {
      abortPromiseOnUnmount(promise);
    };
  }, [dispatch]);
  useEffect(() => {
    const promise: unknown = dispatch(fetchSummerActivitySchoolYears());
    return () => {
      abortPromiseOnUnmount(promise);
    };
  }, [dispatch]);

  return (
    <ManualInputPage<SummerActivityModel, SummerActivityFormViewModel>
      actions={ManualInputCrudAction.CREATE_AND_EDIT}
      loadingOptions={{
        isLoading: isLoading,
        loadingDataId: 'summer-activity-input-loader',
      }}
      navigationTabs={buildUserAccessNavigationTabItems(Tabs_STUDENT)}
      classes={{ pageClass: 'data-input--summer-activity--input' }}
      parameterOptions={{
        routeParameterName: 'summerActivityId',
        identityName: summerActivityModel.id,
      }}
      routes={{
        backBarRoute: buildReturnUrl(),
        cancelRoute: buildReturnUrl(),
        createSuccessRoute: appRoutePaths.SummerActivityInput,
      }}
      dataFunctions={{
        loadDataDetailById: fetchSummerActivityById,
        loadFormDetail: fetchSummerActivityFormViewModel,
        setEntryMode: setSummerActivityDataEntryMode,
        setData: saveSummerActivity,
        clearData: clearSummerActivity,
        onCreate: createSummerActivity,
        onUpdate: updateSummerActivity,
      }}
      formDetails={{
        validationSchema: SummerActivityValidationSchema,
        initialFormValues: summerActivityModel,
        isFormModelEmpty: !formViewModel.verifiedOptions?.length,
      }}
      config={{
        dataEntryMode: dataEntryMode,
        objectVerbiage: 'Summer Activity',
        pageHeader: 'Student',
        supportedNonGenericErrors: supportedNonGenericErrors,
      }}
      DEV_MODE={GLOBAL_DEBUG_MODE}
    >
      <div data-testid="summer-activity-input-child">
        <Panel panelType={PanelType.INFO} heading="Student Information">
          <div className="field-row field-row-1-1-2-1">
            <FormTextbox displayName="MSIS ID" field="msisId" disabled={true} />
            <FormTextbox
              displayName="Grade Level"
              field="gradeLevel"
              disabled={true}
            />
            <FormTextbox
              displayName="Student Name"
              field="fullName"
              disabled={true}
            />
            <FormDateSelector
              displayName="Birthdate"
              field="birthDate"
              disabled={true}
              maxDate={MAX_DATE}
              isClearable
            />
          </div>
        </Panel>
        <Panel panelType={PanelType.INFO} heading="LEA and School Information ">
          <div className="field-row field-row-1-1-2-1">
            <FormTextbox
              displayName="School Year"
              field="schoolYear"
              validationSchema={SummerActivityValidationSchema}
              disabled={true}
            />
            <FormTextbox
              displayName="LEA Identifier"
              field="leaIdentifier"
              validationSchema={SummerActivityValidationSchema}
              disabled={true}
            />
            <FormTextbox
              displayName="LEA Name"
              field="leaName"
              validationSchema={SummerActivityValidationSchema}
              disabled={true}
            />
          </div>

          <div className="field-row field-row-1-2-2">
            <FormTextbox
              displayName="School Identifier"
              field="schoolIdentifier"
              validationSchema={SummerActivityValidationSchema}
              disabled={true}
            />
            <FormTextbox
              displayName="School Name"
              field="schoolName"
              validationSchema={SummerActivityValidationSchema}
              disabled={true}
            />
          </div>
        </Panel>
        <Panel panelType={PanelType.INFO} heading="Summer Activity">
          <div className="field-row field-row-4-1-1">
            <FormDropDown
              displayName="Reason Code and Description"
              field="reasonCode"
              disabled={true}
              options={formViewModel.reasonCodeDescriptionOptions}
            />
            <FormDropDown
              displayName="Verified"
              field="verified"
              disabled={!summerActivityModel.studentUpdate}
              options={formViewModel.verifiedOptions}
            />
          </div>
        </Panel>
      </div>
    </ManualInputPage>
  );
};

export default SummerActivityInput;
